export const defaultProfile = {
  id: 'profile',
  name: 'Tejas Patne',
  subtitle: 'Fullstack Developer | SDE @CarWale',
  summary: 'Software Development Engineer with 2 years of experience building and maintaining production applications at scale. Experienced in solving performance challenges, client-server integrations & production issue resolution, with hands-on expertise in Java, Spring Boot, REST APIs, Microservices, and CI/CD. Seeking Software Engineer roles focused on backend or full-stack development.',
  resumeUrl: 'https://drive.google.com/file/d/1mC-6anMcoAsIMsBFX2yTupvp5wWFAkWN/view?usp=sharing',
  contact: {
    email: 'tejaspatne18@gmail.com',
    linkedinName: 'Tejas Patne',
    phone: '+91 8169622343'
  },
  links: {
    linkedin: 'https://www.linkedin.com/in/tejas-patne/',
    github: 'https://github.com/TejasPatne',
    leetcode: 'https://leetcode.com/u/TejasPatne/',
    whatsapp: 'https://api.whatsapp.com/send?phone=918169622343'
  }
}

export const defaultExperience = [
  {
    id: 'carwale-sde',
    position: 'Software Development Engineer',
    company: 'CarWale',
    period: 'Sep 2024 – Present',
    location: 'Mumbai, India',
    achievements: [
      'Led the redesign of the vehicle listing and filtering experience across CarWale and BikeWale (1.1M+ combined MAU), shipping new features alongside rendering and state-management optimizations that improved vehicle detail page CTR by 18%, cars explored per session by 27%, and reduced listing page bounce rate by 14%.',
      'Redesigned the mobile push notification pipeline by migrating to Notifee and introducing a new data-driven notification flow; defined payload requirements with the notification platform team, improving delivery reliability and increasing notification CTR from 7–9% to 20–24%.',
      'Led the React Native upgrade from v0.77 to v0.83 and investigated production-only stability issues after rollout, identifying and fixing critical crashes across subsequent releases to improve crash-free users from 94% to 99.8%.',
      'Built an offline-resilient analytics delivery mechanism that persisted failed tracking events locally and retried them in batches when connectivity was restored, preventing loss of business-critical analytics events.',
      'Recognized as Developer of the Month (April 2026) for exceptional engineering contributions and ownership.'
    ]
  }
]

export const defaultProjects = [
  {
    id: 1,
    title: 'POS System Backend',
    subtitle: 'Enterprise Point-of-Sale Platform',
    technologies: ['Java', 'Spring Boot', 'Spring Security', 'PostgreSQL', 'Docker', 'Flyway', 'GitHub Actions'],
    highlights: [
      'RESTful API supporting 6+ domain entities with complex business logic',
      'JWT authentication with RBAC for 4+ user roles',
      'PostgreSQL migration with versioned Flyway migrations',
      'Automated CI/CD with Docker & GitHub Actions'
    ],
    fullAchievements: [
      'Built and deployed a RESTful Point-of-Sale backend supporting stores, branches, products, inventory, customers, orders, refunds, and role-based user management.',
      'Implemented JWT-based authentication and role-based access control across multiple roles, including store administrators, branch managers, cashiers, and customers.',
      'Added order and shift-reporting workflows to track sales, order counts, payment-type summaries, refunds, recent orders, and top-selling products.',
      'Migrated the application from MySQL to PostgreSQL for cloud deployment and introduced Flyway-based versioned database migrations, replacing environment-dependent schema creation with production-oriented schema validation.',
      'Containerized the application with Docker and implemented a GitHub Actions release pipeline that builds versioned Docker images, pushes them to Docker Hub, triggers deployment on Render, and tags release commits.',
      'Added Swagger/OpenAPI documentation and Spring Boot Actuator to improve API discoverability and operational monitoring.'
    ],
    links: {
      code: 'https://github.com/TejasPatne/POS-System',
      demo: 'https://pos-system-ydoj.onrender.com/'
    }
  },
  {
    id: 2,
    title: 'Job Portal Microservices Platform',
    subtitle: 'Scalable Service Architecture',
    technologies: ['Spring Boot', 'Spring Cloud', 'PostgreSQL', 'RabbitMQ', 'Docker Compose', 'Eureka', 'Zipkin'],
    highlights: [
      '3 independently deployable microservices',
      'Service discovery with Eureka & Cloud Gateway',
      'Hybrid sync/async communication patterns',
      'Distributed tracing & rate limiting'
    ],
    fullAchievements: [
      'Built a job portal using 3 independently deployable Spring Boot services for Job, Company, and Review management, with separate service infrastructure for API Gateway, configuration, and service discovery.',
      'Implemented service discovery with Eureka, centralized configuration with Spring Cloud Config, and request routing through Spring Cloud Gateway.',
      'Integrated synchronous service-to-service communication using OpenFeign and asynchronous communication using RabbitMQ for review-driven company rating updates.',
      'Applied Resilience4j rate limiting and implemented distributed tracing with Micrometer and Zipkin to explore resilience and observability patterns in a microservices architecture.',
      'Containerized supporting infrastructure with Docker Compose for reproducible local development and orchestration.'
    ],
    links: {
      code: 'https://github.com/TejasPatne/FirstJobApplication'
    }
  }
]

export const defaultSkillCategories = [
  {
    id: 'languages',
    title: 'Languages',
    color: '#6366f1',
    description: 'Core programming languages for building scalable applications',
    skills: ['Java', 'SQL', 'TypeScript', 'JavaScript'],
    useCases: ['Type-Safe Development', 'Full-Stack Programming', 'Data Querying']
  },
  {
    id: 'backend',
    title: 'Backend & Architecture',
    color: '#10b981',
    description: 'Enterprise frameworks and patterns for microservices and distributed systems',
    skills: ['Spring Boot', 'Spring Data JPA', 'Hibernate', 'Spring Security', 'Spring Cloud', 'REST APIs', 'OpenFeign', 'Resilience4j'],
    useCases: ['Microservices Architecture', 'Service Communication', 'API Security']
  },
  {
    id: 'data',
    title: 'Data & Messaging',
    color: '#ec4899',
    description: 'Databases and messaging systems for data persistence and async communication',
    skills: ['MySQL', 'MongoDB', 'Redis', 'RabbitMQ', 'Firebase'],
    useCases: ['Data Persistence', 'Async Messaging', 'Caching Strategies']
  },
  {
    id: 'concepts',
    title: 'Core Concepts',
    color: '#f59e0b',
    description: 'Architectural patterns and design principles for robust systems',
    skills: ['Microservices', 'Distributed Systems', 'Event-Driven Architecture', 'System Design', 'OOP', 'Design Patterns'],
    useCases: ['System Design', 'Scalability Patterns', 'Resilience Engineering']
  },
  {
    id: 'devops',
    title: 'DevOps & Observability',
    color: '#8b5cf6',
    description: 'Containerization, CI/CD pipelines, and production monitoring',
    skills: ['Docker', 'Docker Compose', 'CI/CD', 'GitHub Actions', 'Micrometer', 'Zipkin'],
    useCases: ['Containerization', 'Automated Deployment', 'Observability & Tracing']
  },
  {
    id: 'frontend',
    title: 'Frontend',
    color: '#06b6d4',
    description: 'Modern UI frameworks and styling for web and mobile applications',
    skills: ['React.js', 'React Native', 'HTML', 'CSS', 'Tailwind'],
    useCases: ['Responsive Interfaces', 'Cross-Platform Apps', 'Performance Optimization']
  },
  {
    id: 'tools',
    title: 'Developer Tools',
    color: '#3b82f6',
    description: 'Essential tools for version control, API testing, and project management',
    skills: ['Git', 'GitHub', 'Postman', 'Jira', 'IntelliJ IDEA', 'Claude Code'],
    useCases: ['Version Control', 'API Development', 'Team Collaboration']
  }
]
